import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { ChartBar, ChartLine, Table as TableIcon } from '@phosphor-icons/react';

import Card from 'partial/components/Card';
import Table from 'partial/components/Table';
import { BarChart, HorizontalBarChart } from 'modules/common/chart/BarChart';
import LineChart from 'modules/common/chart/LineChart';
import HistogramRange from 'partial/components/HistogramRange';
import PieChart from 'modules/common/chart/PieChart';
import EmptyState from 'partial/components/EmptyState';

const VIEWS = [
  { id: 'table', Icon: TableIcon, label: 'Table view' },
  { id: 'line', Icon: ChartLine, label: 'Line chart' },
  { id: 'bar', Icon: ChartBar, label: 'Bar chart' },
];

export const useHistogramCard = (array, defaultIndex = 0) => {
  const [props, setProps] = React.useState(array[defaultIndex]);
  const output = React.useMemo(
    () => ({
      key: props.id,
      ...props,
      actionOptions: array.map((x) => ({
        id: x.id,
        label: x.label,
        onClick: () => {
          setProps(x);
        },
      })),
    }),
    [array, props, setProps],
  );
  return output;
};

const MAP_TYPE = {
  day: 'hour',
  month: 'day',
  year: 'month',
};

function HistogramCard({
  hook,
  label,
  dateRange,
  switchMode,
  defaultSort,
  defaultMode,
  tableFormat,
  defaultChart,
  actionOptions,
  filter: otherFilter,
  renderSummary,
  customHeight,
  cardClassName,
  containerClass,
  extraParam,
}) {
  const [chartType, setChartType] = React.useState(defaultChart);
  const [meta, setMeta] = React.useState({ sort: defaultSort });
  const [filter, setFilter] = React.useState(() => ({
    type: MAP_TYPE[defaultMode],
    from:
      otherFilter?.from || dayjs().startOf(defaultMode).format('YYYY-MM-DD'),
    to: otherFilter?.to || dayjs().endOf(defaultMode).format('YYYY-MM-DD'),
  }));
  const newFilter = React.useMemo(
    () => ({
      ...otherFilter,
      ...filter,
    }),
    [otherFilter, filter],
  );

  const [isLoading, table, chart, subLabel = '', summary = {}] = hook(
    newFilter,
    extraParam,
  );
  const toggleChart = React.useMemo(
    () => (
      <div className="flex flex-wrap gap-1 items-center justify-end">
        {dateRange && (
          <HistogramRange
            onChange={setFilter}
            defaultValue={dayjs().format('YYYY-MM-DD')}
            defaultMode={defaultMode}
            switchMode={switchMode}
          />
        )}
        {/* Segmented control — 28px cells, one border, active cell fills. */}
        <div className="inline-flex rounded border border-neutral-300 overflow-hidden divide-x divide-neutral-300 flex-shrink-0">
          {VIEWS.map(({ id, Icon, label: viewLabel }) => (
            <button
              key={id}
              type="button"
              aria-label={viewLabel}
              aria-pressed={chartType === id}
              onClick={() => setChartType(id)}
              className={cn(
                'h-7 w-8 inline-flex items-center justify-center transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600',
                chartType === id
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900',
              )}
            >
              <Icon size={16} />
            </button>
          ))}
        </div>
      </div>
    ),
    [setFilter, chartType, setChartType, defaultMode, switchMode, dateRange],
  );

  // Empty and error states are the shared block, never an Apex "no data" string.
  const isEmpty =
    !isLoading && (chartType === 'table' ? !table?.length : !chart?.length);

  const summaryFunc = () => {
    if (typeof renderSummary === 'function') return renderSummary(summary);
    return null;
  };

  return (
    <Card
      label={label}
      actionOptions={actionOptions}
      renderAction={renderSummary ? summaryFunc() : toggleChart}
      className={cardClassName}
    >
      <div
        className={cn('flex flex-col pr-3', containerClass)}
        style={{ height: customHeight ?? 300 }}
      >
        {isEmpty && (
          <EmptyState
            statement="No data in this period."
            guidance="Volume appears once there is activity to chart."
          />
        )}
        {!isEmpty && chartType === 'table' && (
          <Table
            isLoading={isLoading}
            data={table}
            format={tableFormat}
            downloadCsv={(row) => ({
              Label: row.label,
              Txn: row.transaction,
              Amount: row.amount,
            })}
            downloadFilename={label}
            onSort={meta.sort.indexOf('id') > -1 ? false : setMeta}
            sort={meta.sort}
            subLabel={subLabel}
          />
        )}
        {!isEmpty && chartType === 'line' && (
          <LineChart data={chart} isLoading={isLoading} />
        )}
        {!isEmpty && chartType === 'bar' && (
          <BarChart data={chart} horizontal={false} isLoading={isLoading} />
        )}
        {!isEmpty && chartType === 'horizontal-bar' && (
          <HorizontalBarChart data={chart} horizontal isLoading={isLoading} />
        )}
        {!isEmpty && chartType === 'pie' && (
          <PieChart data={chart} isLoading={isLoading} />
        )}
      </div>
    </Card>
  );
}

HistogramCard.defaultProps = {
  filter: {},
  label: 'Chart',
  dateRange: true,
  switchMode: true,
  actionOptions: [],
  defaultMode: 'year',
  defaultChart: 'line',
  defaultSort: 'id:asc',
  tableFormat: [
    { key: 'name', label: 'Name' },
    { key: 'messages', label: 'Messages', width: 120 },
  ],
  renderSummary: null,
  customHeight: null,
  containerClass: '',
  cardClassName: '',
  extraParam: '',
};

HistogramCard.propTypes = {
  label: PropTypes.string,
  dateRange: PropTypes.bool,
  switchMode: PropTypes.bool,
  defaultMode: PropTypes.string,
  defaultSort: PropTypes.string,
  defaultChart: PropTypes.string,
  hook: PropTypes.func.isRequired,
  filter: PropTypes.instanceOf(Object),
  tableFormat: PropTypes.instanceOf(Array),
  actionOptions: PropTypes.instanceOf(Array),
  renderSummary: PropTypes.func,
  customHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containerClass: PropTypes.string,
  cardClassName: PropTypes.string,
  extraParam: PropTypes.string,
};

export default HistogramCard;
